/**
 * Client-side error handling. Spec 22.2 and 22.3.
 *
 * "Codes are stable identifiers. Clients switch on codes, never on messages — messages are
 * localised and change freely." Every message the user sees comes from an i18n key derived
 * from the code; the server's message is only ever a developer-facing fallback.
 */
import { ErrorCode } from '@lila/shared';
import { TRPCClientError } from '@trpc/client';

export type AppErrorShape = {
  readonly code: ErrorCode;
  readonly message: string;
  readonly correlationId: string;
  readonly fields?: Readonly<Record<string, string[]>>;
};

/** Spec 22.3's behaviour table, as a type the compiler can check for exhaustiveness. */
export type ErrorBehaviour =
  | { readonly action: 'resync-and-retry-once'; readonly notify: false }
  | { readonly action: 'toast'; readonly severity: 'info' | 'warning' | 'error' }
  | { readonly action: 'toast-with-countdown' }
  /*
   * `cta: 'none'` was added for RULE_SET_UNSAFE. An inline panel is the right shape for a
   * refusal the user must read alongside its evidence, and not every such refusal is about
   * money — sending an admin to the billing page because their rule set does not terminate
   * would be nonsense.
   */
  | { readonly action: 'inline-panel'; readonly cta: 'billing' | 'none' }
  | { readonly action: 'inline-field' }
  | { readonly action: 'degrade'; readonly fallback: 'show-square-commentary' }
  | { readonly action: 'modal'; readonly cta: 'billing' }
  | { readonly action: 'redirect-login'; readonly preserveReturnTo: true }
  | { readonly action: 'error-boundary'; readonly showCorrelationId: true }
  | { readonly action: 'route-away' };

/**
 * Spec 22.3. Keyed by `ErrorCode` with no index signature, so adding a code to
 * `@lila/shared` without deciding how the UI reacts to it is a compile error rather than a
 * silent fallthrough to a generic toast.
 */
export const ERROR_BEHAVIOUR: Record<ErrorCode, ErrorBehaviour> = {
  // auth
  [ErrorCode.UNAUTHENTICATED]: { action: 'redirect-login', preserveReturnTo: true },
  [ErrorCode.INVALID_CREDENTIALS]: { action: 'inline-field' },
  [ErrorCode.EMAIL_NOT_VERIFIED]: { action: 'inline-panel', cta: 'billing' },
  [ErrorCode.EMAIL_IN_USE]: { action: 'inline-field' },
  /* Spec 22.5 classes suspension as unrecoverable: route away with an explanation. */
  [ErrorCode.ACCOUNT_SUSPENDED]: { action: 'route-away' },
  [ErrorCode.TOKEN_EXPIRED]: { action: 'inline-panel', cta: 'billing' },
  [ErrorCode.MFA_REQUIRED]: { action: 'inline-field' },

  // authorization
  [ErrorCode.FORBIDDEN]: { action: 'toast', severity: 'warning' },
  [ErrorCode.NOT_SESSION_HOST]: { action: 'toast', severity: 'warning' },
  [ErrorCode.NOT_PARTICIPANT]: { action: 'route-away' },
  [ErrorCode.ROLE_REQUIRED]: { action: 'toast', severity: 'warning' },

  // session
  [ErrorCode.SESSION_NOT_FOUND]: { action: 'route-away' },
  [ErrorCode.SESSION_FULL]: { action: 'inline-panel', cta: 'billing' },
  [ErrorCode.SESSION_CLOSED]: { action: 'route-away' },
  [ErrorCode.SESSION_NOT_STARTED]: { action: 'toast', severity: 'info' },
  [ErrorCode.ALREADY_JOINED]: { action: 'toast', severity: 'info' },
  [ErrorCode.INVALID_SESSION_STATE]: { action: 'toast', severity: 'warning' },
  [ErrorCode.NO_PARTICIPANTS]: { action: 'toast', severity: 'info' },
  /* §6.6: the session has begun and is closed to late joins — send the arrival away with
   * a clear explanation rather than leaving them on a form that will keep failing. */
  [ErrorCode.SESSION_LOCKED]: { action: 'route-away' },
  /* §5.4: too many players for an AI group — a corrigible form error on the seats field. */
  [ErrorCode.AI_GROUP_TOO_LARGE]: { action: 'inline-field' },
  /* §6.1: the practice cap is reached; a warning, not a hard failure. */
  [ErrorCode.PRACTICE_LIMIT_REACHED]: { action: 'toast', severity: 'warning' },

  // gameplay
  /*
   * Spec 22.3: "NOT_YOUR_TURN is info, not error. It is a normal thing to do by accident."
   */
  [ErrorCode.NOT_YOUR_TURN]: { action: 'toast', severity: 'info' },
  [ErrorCode.ALREADY_COMPLETED]: { action: 'toast', severity: 'info' },
  [ErrorCode.INTENTION_REQUIRED]: { action: 'inline-field' },
  /*
   * Spec 22.3: "STALE_STATE is invisible. Showing an error would be noise about a
   * condition the user did not cause and cannot act on."
   */
  [ErrorCode.STALE_STATE]: { action: 'resync-and-retry-once', notify: false },
  [ErrorCode.INVALID_POSITION]: { action: 'toast', severity: 'error' },
  [ErrorCode.REFLECTION_NOT_EXPECTED]: { action: 'toast', severity: 'info' },
  /*
   * ADR-0007. Inline rather than a toast: the answer is right there on screen — the
   * reflection editor is already open — so the message belongs beside the roll control
   * that just refused, not floating over the board.
   */
  [ErrorCode.REFLECTION_REQUIRED]: { action: 'inline-field' },

  // billing
  [ErrorCode.NO_CREDITS]: { action: 'modal', cta: 'billing' },
  [ErrorCode.SUBSCRIPTION_REQUIRED]: { action: 'modal', cta: 'billing' },
  [ErrorCode.PAYMENT_FAILED]: { action: 'inline-panel', cta: 'billing' },
  [ErrorCode.PLAN_NOT_FOUND]: { action: 'toast', severity: 'error' },
  /*
   * AMENDMENT 001: while `BILLING_ENABLED=false` every payment CTA opens a "coming soon"
   * dialog, so this code should never reach a user. If it does — a checkout procedure
   * called directly, a webhook probed by hand — an inline panel says what happened without
   * pretending the account has a problem.
   */
  [ErrorCode.BILLING_DISABLED]: { action: 'inline-panel', cta: 'billing' },
  /*
   * Never reaches a browser: this is the answer to a forged webhook, and the only client
   * is the payment provider's server. It is here because the record is exhaustive, and a
   * behaviour is required even for a code the UI should never see.
   */
  [ErrorCode.WEBHOOK_SIGNATURE_INVALID]: { action: 'toast', severity: 'error' },

  // mfa
  /* Inline: the six-digit field is on screen and retyping is the whole remedy. */
  [ErrorCode.MFA_INVALID_CODE]: { action: 'inline-field' },
  [ErrorCode.MFA_ALREADY_ENROLLED]: { action: 'toast', severity: 'warning' },

  // content
  /*
   * Spec 12.2 gives the rule-set editor a "simulation results" state, and this code means
   * the results said no. An inline panel keeps the report and the refusal on the same
   * screen; a toast would dismiss the one piece of information the admin needs to act on.
   */
  [ErrorCode.RULE_SET_UNSAFE]: { action: 'inline-panel', cta: 'none' },

  // ai
  [ErrorCode.AI_QUOTA_EXCEEDED]: { action: 'inline-panel', cta: 'billing' },
  [ErrorCode.AI_SESSION_LIMIT_REACHED]: { action: 'inline-panel', cta: 'billing' },
  /* The trial spend cap is a billing wall: play continues on commentary, and the panel sells the upgrade. */
  [ErrorCode.AI_TRIAL_LIMIT_REACHED]: { action: 'inline-panel', cta: 'billing' },
  /*
   * Spec 22.3: "AI_UNAVAILABLE degrades rather than fails. A paid solo session must never
   * become unusable because a third-party API is down."
   */
  [ErrorCode.AI_UNAVAILABLE]: { action: 'degrade', fallback: 'show-square-commentary' },
  [ErrorCode.AI_TIMEOUT]: { action: 'degrade', fallback: 'show-square-commentary' },

  // audio (chat/audio feature)
  /*
   * Audio never blocks the game (spec: audio is never required to play). Both codes are a
   * quiet toast; the audio control surfaces the external call link on its own.
   */
  [ErrorCode.AUDIO_UNAVAILABLE]: { action: 'toast', severity: 'info' },
  [ErrorCode.AUDIO_ROOM_FULL]: { action: 'toast', severity: 'warning' },

  // generic
  [ErrorCode.VALIDATION_ERROR]: { action: 'inline-field' },
  [ErrorCode.RATE_LIMITED]: { action: 'toast-with-countdown' },
  [ErrorCode.NOT_FOUND]: { action: 'route-away' },
  [ErrorCode.CONFLICT]: { action: 'toast', severity: 'warning' },
  [ErrorCode.INTERNAL_ERROR]: { action: 'error-boundary', showCorrelationId: true },
};

/** Spec 22.5: recoverable errors keep the user in place; unrecoverable ones replace the view. */
const UNRECOVERABLE = new Set<ErrorCode>([
  ErrorCode.SESSION_NOT_FOUND,
  ErrorCode.SESSION_CLOSED,
  ErrorCode.SESSION_LOCKED,
  ErrorCode.ACCOUNT_SUSPENDED,
  ErrorCode.NOT_PARTICIPANT,
  ErrorCode.NOT_FOUND,
  ErrorCode.UNAUTHENTICATED,
  ErrorCode.FORBIDDEN,
]);

export function isRecoverable(code: ErrorCode): boolean {
  return !UNRECOVERABLE.has(code);
}

/**
 * A command that failed over the socket.
 *
 * Spec 9.3 acknowledges every client command, and a failed ack carries the same
 * `{ code, message, correlationId }` an HTTP error does. Wrapping it in an `Error` lets
 * the socket transport reject a promise, so `commands.ts` handles both transports with one
 * try/catch and one `STALE_STATE` retry path.
 */
export class SocketCommandError extends Error {
  readonly appError: AppErrorShape;

  constructor(appError: AppErrorShape) {
    super(appError.message);
    this.name = 'SocketCommandError';
    this.appError = appError;
  }
}

/**
 * Pulls the server's structured error out of whatever the client threw. Returns `null`
 * for a transport failure, which is a different situation — the request may not have
 * reached the server at all.
 */
export function appErrorFrom(error: unknown): AppErrorShape | null {
  if (error instanceof SocketCommandError) return error.appError;
  if (!(error instanceof TRPCClientError)) return null;

  const data: unknown = error.data;
  if (typeof data !== 'object' || data === null || !('appError' in data)) return null;

  const appError: unknown = data.appError;
  if (typeof appError !== 'object' || appError === null) return null;

  const record = appError as Record<string, unknown>;
  const code = record['code'];
  if (typeof code !== 'string' || !(code in ERROR_BEHAVIOUR)) return null;

  const details = record['details'];
  const fields =
    typeof details === 'object' && details !== null && 'fields' in details
      ? (details as { fields: Record<string, string[]> }).fields
      : undefined;

  return {
    code: code as ErrorCode,
    message: typeof record['message'] === 'string' ? record['message'] : '',
    correlationId: typeof record['correlationId'] === 'string' ? record['correlationId'] : '',
    ...(fields === undefined ? {} : { fields }),
  };
}

export function behaviourFor(error: unknown): ErrorBehaviour {
  const appError = appErrorFrom(error);
  /* A transport failure is not an internal server error, but it lands in the same place. */
  return appError === null
    ? { action: 'error-boundary', showCorrelationId: true }
    : ERROR_BEHAVIOUR[appError.code];
}

/**
 * The i18n key for an error. Spec 22.2: the client never renders the server's message, so
 * this is the only path from a failure to something a user reads.
 */
export function errorMessageKey(code: ErrorCode): string {
  return `errors:${code}`;
}

/**
 * The i18n key for anything thrown, including a transport failure.
 *
 * Spec 22.2: "Clients switch on codes, never on messages — messages are localised and
 * change freely." A component that renders `error.message` has quietly opted out of
 * translation, so this is the only route from a rejected mutation to visible text.
 */
export function errorKeyFor(error: unknown): string {
  return errorMessageKey(appErrorFrom(error)?.code ?? ErrorCode.INTERNAL_ERROR);
}
